// Table
import Table from '../ui/table/Table'

// API
import { useApiSWRReference } from '@/hooks/useApiSWR'

// Mantine
import { Anchor, RingProgress } from '@mantine/core'

// UI Components
import Badge from '../ui/Badge'

// Dayjs
import dayjs from 'dayjs'

// Lodash
import { maxBy, minBy, sumBy } from 'lodash'

// Functions
import { statusName, statusDotColor } from './purchaseOrderStatuses'

// Cell
const IdCell = (item) => {
    return (
        <div className="table-cell p-5 align-middle">
            <div className="flex flex-col">
                <Anchor component="span">BC{item.id}</Anchor>
            </div>
        </div>
    )
}

const StatusCell = (item) => {
    return (
        <div className="table-cell p-5 align-middle">
            <div className="flex justify-end gap-2">
                <Badge size="sm" color={statusDotColor(item.status)}>
                    {statusName(item.status)}
                </Badge>
            </div>
        </div>
    )
}

const ReferenceCell = (item) => {
    /*
    const maxReceptionAt = maxBy(
        item.lines,
        'reception_at'
    )?.reception_at

    const minByReceptionAt = minBy(
        item.lines,
        'reception_at'
    )?.reception_at
    */

    return (
        <div className="table-cell p-5 align-middle">
            <div className="flex flex-col">
                <span className="text-sm text-gray-800 truncate">
                    {item.reference + ' - ' + item.name}
                </span>

                <span className="text-sm text-gray-400">
                    {item.supplier?.name}
                </span>

                <div className="flex gap-1 items-center mt-2 text-sm text-gray-400">
                    <span>{item.location.name} - </span>

                    <div>{dayjs(item.min_reception_at).format('ll')}</div>
                </div>
            </div>
        </div>
    )
}

const NumberOfLinesCell = (item) => {
    // Check if it has line items
    if (item.lines.length === 0) {
        return (
            <div className="table-cell p-5 align-middle">
                <div className="flex gap-2 items-center">
                    <span className="text-sm text-gray-500">
                        Aucune réception
                    </span>
                </div>
            </div>
        )
    }

    const maxReceptionAt = maxBy(
        item.lines,
        'reception_at'
    ).reception_at

    const minByReceptionAt = minBy(
        item.lines,
        'reception_at'
    ).reception_at

    return (
        <div className="table-cell p-5 align-middle text-sm text-gray-500">
            {maxReceptionAt === minByReceptionAt && (
                <span>{dayjs(maxReceptionAt).format('ll')}</span>
            )}

            {maxReceptionAt !== minByReceptionAt && (
                <span>
                    {dayjs(minByReceptionAt).format('ll')} —{' '}
                    {dayjs(maxReceptionAt).format('ll')}
                </span>
            )}
        </div>
    )
}

const ProgressCell = (item) => {
    const totalToReceive = sumBy(
        item.lines,
        'quantity_to_receive'
    )

    const totalReceived = sumBy(
        item.lines,
        'quantity_received'
    )

    // Get the ratio of progress (maximum 1)
    const progress = Math.min(totalReceived / totalToReceive, 1) * 100

    return (
        <div className="table-cell p-5 align-middle">
            <RingProgress
                size={40}
                sections={[{ value: item.progress, color: 'green' }]}
            />
        </div>
    )
}

const LocationCell = (item) => {
    return (
        <div className="table-cell p-5 align-middle">
            <span className="text-sm text-gray-500">{item.location?.name}</span>
        </div>
    )
}

export default function PurchaseOrdersTable() {
    // Set columns available for the table
    const availableColumns = [
        {
            name: 'Numéro',
            key: 'id',
            display: 'mandatory',
            sortable: true,
            width: 200,
            component: IdCell,
        },
        {
            name: 'Détail',
            key: 'min_reception_at',
            display: 'optional_default',
            sortable: true,
            component: ReferenceCell,
        },
        {
            name: 'Progression',
            key: 'progress',
            display: 'optional_default',
            component: ProgressCell,
        },
        {
            name: 'Lieu',
            key: 'location',
            display: 'optional',
            component: LocationCell,
        },
        {
            name: 'Statut',
            key: 'status',
            display: 'optional_default',
            sortable: true,
            component: StatusCell,
        },
    ]

    // Load filters data
    const { data: component_categories } =
        useApiSWRReference('/component-categories')

    // Filters
    const availableFilters = [
        {
            name: 'Statut',
            key: 'status',
            type: 'multiselect',
            placeholder: 'À réceptionner, annulés ou terminés',
            options: ['open', 'closed', 'canceled'].map((status) => ({
                value: status,
                label: statusName(status),
            })),
        },
    ]

    return (
        <Table
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            engineApiEndpoint="purchase-orders"
            linkItemUrl="/purchase_orders"
            linkItemId="id"
            withLocation={true}
            withLocationAll={true}
            searchable={true}
        />
    )
}
